import { useEffect } from 'react'
import { useQuery, gql } from '@apollo/client'
import { defineMessages, useIntl, FormattedMessage } from 'react-intl'
import { NavLink, useLocation, useParams } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { ContentBody } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { CartTabs } from 'features/Carts/components/Tabs'
import { InventoryActions } from 'features/Inventories/components/InventoryActions'
import { InventoryHeader } from 'features/Inventories/components/InventoryHeader'
import { InventoryContent } from 'features/Inventories/components/InventoryContent'
import { withOutboundOrders } from 'features/Orders/components/WithOutboundOrders'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { useSanitizeItem } from 'resolvers/items'
import { getQueries } from 'utils/route'
import { canReadCart, canWriteCart } from 'features/Users/utils'
import { OutboundOrdersFilters } from './Filters'
import { OutboundOrdersList } from './List'
import { OutboundOrdersPagination } from './Pagination'
import { useLogin } from 'features/Login/hooks'

const messages = defineMessages({
  helmet: {
    id: 'carts.outboundOrders.helmet',
    defaultMessage: 'Outbound orders | Nexbus Admin'
  }
})

const GET_CART = gql`
  query GetCart($cartId: ID!) {
    cart(input: { id: $cartId }) {
      id
      name
    }
  }
`

const CartOutboundOrders = withOutboundOrders(OutboundOrdersList, OutboundOrdersPagination)

const CartOutoundOrdersView = (): TSFixMe => {
  const location = useLocation()
  const { cartId } = useParams()
  const intl = useIntl()

  const login = useLogin()
  const hasReadPermission = canReadCart(login.accessToken.policies)
  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const { data: cartData } = useQuery(GET_CART, {
    variables: {
      cartId
    },
    skip: !hasReadPermission
  })
  // eslint-disable-next-line prefer-const
  let { page = 1, id = '', conciliationStatus = '', itemId = '' } = getQueries(location.search)
  const { companyId } = login.accessToken
  const sanitizedItem = useSanitizeItem(itemId, companyId)

  if (!hasReadPermission) {
    return <ForbiddenView feature={FEATURES.INVENTORY} />
  }

  // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
  page = parseInt(page)

  return (
    <>
      <InventoryHeader inventory={cartData && cartData.cart} />
      <CartTabs cartId={cartId} />
      <InventoryContent>
        {canWriteCart(login.accessToken.policies) && (
          <InventoryActions>
            <Button as={NavLink} to={`/carts/${cartId}/remove-bulk-items`} kind="primary">
              <FormattedMessage id="spanish.carts.outbounds.actions.create" defaultMessage="Agregar salida" />
            </Button>
          </InventoryActions>
        )}
        <ContentBody>
          <OutboundOrdersFilters
            filters={{
              id,
              conciliationStatus,
              item: sanitizedItem
            }}
            cartId={cartId}
            companyId={companyId}
          />
          <CartOutboundOrders
            page={page}
            inventoryId={cartId}
            cart={cartData && cartData.cart}
            cartId={cartId}
            urls={{
              outboundOrders: `/carts/${cartId}/outbound-orders`,
              createOrder: `/carts/${cartId}/remove-bulk-items`
            }}
            filters={{
              id,
              conciliationStatus,
              itemId
            }}
          />
        </ContentBody>
      </InventoryContent>
    </>
  )
}

export default withLastUrl(CartOutoundOrdersView)
